import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";

/**
 * Which company the app is currently looking at, and which sender it composes
 * as within that company.
 *
 * The brand is the one an operator actually thinks in — PayKings, Pulse,
 * Payment Review are separate businesses with separate reputations — so it
 * scopes the dashboard as well as the sending identity. The sender follows from
 * it rather than being chosen independently: a sender belongs to a domain,
 * which belongs to exactly one brand, and the pair going out of step is the
 * mistake this model exists to prevent.
 *
 * Kept in memory rather than persisted. A stored id goes stale when a brand or
 * domain is removed, and silently acting as the wrong company is worse than
 * starting from the first one each session.
 */
interface Identity {
  brandId: string | null;
  setBrandId: (id: string | null) => void;
  senderId: string | null;
  setSenderId: (id: string) => void;
}

const Context = createContext<Identity | null>(null);

export function SendingIdentityProvider({ children }: { children: ReactNode }) {
  const [brandId, setBrandIdState] = useState<string | null>(null);
  const [senderId, setSenderId] = useState<string | null>(null);

  // Changing company drops the sender with it, so the next screen picks one
  // belonging to the brand now selected rather than keeping the old one.
  const setBrandId = useCallback((id: string | null) => {
    setBrandIdState(id);
    setSenderId(null);
  }, []);

  const value = useMemo(
    () => ({ brandId, setBrandId, senderId, setSenderId }),
    [brandId, senderId, setBrandId],
  );
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useSendingIdentity(): Identity {
  const ctx = useContext(Context);
  if (!ctx) throw new Error("useSendingIdentity must be used inside SendingIdentityProvider");
  return ctx;
}
